import { APPOINTMENT_PREVIEW_FIELD_KEYS } from '@pages/Calendar/data';
import type {
  AppointmentPreviewField,
  AppointmentPreviewFieldKey,
} from '@pages/Calendar/data';

const isAppointmentPreviewFieldKey = (
  value: unknown
): value is AppointmentPreviewFieldKey =>
  typeof value === 'string' &&
  (APPOINTMENT_PREVIEW_FIELD_KEYS as readonly string[]).includes(value);

// Stored settings may come from an older build: no fields at all, unknown or
// duplicated keys, or a key added later. Valid keys keep their saved order,
// missing ones are appended as visible, so the preview never loses a row.
export const normalizeAppointmentPreviewFields = (
  value: unknown
): AppointmentPreviewField[] => {
  const fields: AppointmentPreviewField[] = [];
  const seenKeys = new Set<AppointmentPreviewFieldKey>();

  if (Array.isArray(value)) {
    value.forEach((item: unknown) => {
      if (typeof item !== 'object' || item === null) return;

      const { key, isVisible } = item as { key?: unknown; isVisible?: unknown };

      if (!isAppointmentPreviewFieldKey(key) || seenKeys.has(key)) return;

      seenKeys.add(key);
      fields.push({ key, isVisible: isVisible !== false });
    });
  }

  APPOINTMENT_PREVIEW_FIELD_KEYS.forEach((key) => {
    if (!seenKeys.has(key)) fields.push({ key, isVisible: true });
  });

  return fields;
};

export const areAppointmentPreviewFieldsEqual = (
  leftFields: AppointmentPreviewField[],
  rightFields: AppointmentPreviewField[]
): boolean =>
  leftFields.length === rightFields.length &&
  leftFields.every(
    ({ key, isVisible }, index) =>
      key === rightFields[index].key &&
      isVisible === rightFields[index].isVisible
  );
